/* eslint-disable react/no-multi-comp */
import HeroVariant2 from "@/components/buildingBlocks/heroVariant2/HeroVariant2";
import ContentWrapper from "@/components/common/contentWrapper/ContentWrapper";
import PageWrapper from "@/components/common/pageWrapper/PageWrapper";
import FullText from "@/components/pages/blogDetail/fullText/FullText";
import BlogArticle from "@/components/pages/blogOverview/blogArticle/BlogArticle";
import { type GetBlogArticlePropsResult } from "@/pages/blog/[slug]";
import { type GetNewsArticlePropsResult } from "@/pages/news/[slug]";
import {
  type IGenBlogArticle,
  type IGenBlogArticleDetailPage,
  type IGenFooter,
  type IGenHeader,
  type IGenNewsArticle,
  type IGenNewsArticleDetailPage, type IGenSeo
} from "@/services/graphql/__generated/sdk";
import { animationProps } from "@/styles/styleVariables";
import { type NewsOrBlog } from "@/utils/types";

import { Button } from "@mui/material";
import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { type FunctionComponent } from "react";

import * as styles from "./BlogArticleDetail.page.style";

interface BlogDetailContentProps
{
  readonly articleDetailPageProps: IGenNewsArticleDetailPage | IGenBlogArticleDetailPage;
  readonly articleProps: IGenNewsArticle | IGenBlogArticle;
  readonly articles: IGenNewsArticle[] | IGenBlogArticle[];
  readonly footer: IGenFooter;
  readonly header: IGenHeader;
  readonly newsOrBlog: NewsOrBlog;
  readonly seo: IGenSeo;
}

const ArticleDetailContent: FunctionComponent<BlogDetailContentProps> = ({
  articleDetailPageProps,
  articleProps,
  articles,
  footer,
  header,
  newsOrBlog,
  seo
}) =>
{
  const { locale } = useRouter();

  if(!articleProps || !articleDetailPageProps)
  {
    return null;
  }

  const selectedDate = articleProps.date;

  let dateToDisplay: Date;

  if(selectedDate)
  {
    dateToDisplay = new Date(selectedDate);
    dateToDisplay.setDate(dateToDisplay.getDate() + 1);
  }
  else
  {
    dateToDisplay = new Date(articleProps._meta?.createdAt);
  }

  const dateToDisplayAsString = dateToDisplay?.toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
  const filteredBlogArticles = articles.slice(0, 3);

  return (
    <div css={styles.wrapper}>
      <PageWrapper
        pageTitle={articleProps.title || ""}
        footerProps={footer}
        websiteSeo={seo}
        headerProps={{
          ...header,
          isHeaderFloating: true
        }}
        pageSeo={articleProps.pageSeo}>
        <HeroVariant2 title={articleProps.title} image={articleProps.image}/>
        <ContentWrapper>
          <motion.div {...animationProps}>
            <div css={styles.buttonAndInfoWrapper}>
              <Button
                component={Link}
                href={`/${newsOrBlog}`}
                variant="contained">
                {articleDetailPageProps.goBackButtonText}
              </Button>
              <div css={styles.infoWrapper}>
                {dateToDisplayAsString && (
                  <div>{dateToDisplayAsString}</div>
                )}
                {articleProps.category?.title && (
                  <div>{articleProps.category?.title}</div>
                )}
              </div>
            </div>
            <div css={styles.separatorLine}/>
            <div css={styles.richtextWrapper}>
              <FullText content={articleProps.content}/>
            </div>
            <div css={styles.separatorLine}/>
          </motion.div>
          <motion.div {...animationProps}>
            <div css={styles.moreNewsTitle}>{articleDetailPageProps.moreNewsTitle}</div>
            <div css={styles.articlesGrid}>
              {filteredBlogArticles.map((article, index) => (
                <div key={article.slug} css={[styles.article, index === 1 && styles.secondArticle, index === 2 && styles.thirdArticle]}>
                  <BlogArticle {...article} newsOrBlog={newsOrBlog}/>
                </div>
              ))}
            </div>
          </motion.div>
        </ContentWrapper>
      </PageWrapper>
    </div>
  );
};

export const BlogArticleDetail: FunctionComponent<GetBlogArticlePropsResult> = ({
  blogArticleDetailPageProps,
  blogArticleProps,
  blogArticles,
  footer,
  header,
  seo
}) => (
  <ArticleDetailContent
    articleDetailPageProps={blogArticleDetailPageProps}
    articleProps={blogArticleProps}
    articles={blogArticles}
    header={header}
    newsOrBlog="blog"
    seo={seo}
    footer={footer}
  />
);

export const NewsArticleDetail: FunctionComponent<GetNewsArticlePropsResult> = ({
  footer,
  header,
  newsArticleDetailPageProps,
  newsArticleProps,
  newsArticles,
  seo
}) => (
  <ArticleDetailContent
    articleDetailPageProps={newsArticleDetailPageProps}
    articleProps={newsArticleProps}
    articles={newsArticles}
    newsOrBlog="news"
    header={header}
    seo={seo}
    footer={footer}
  />
);
